import { capitalizeFirstLetter } from 'utils/string';
import logger from 'utils/logger';
import utils from 'utils/utils';
import { isEmpty } from 'utils/underscore';
import configuration from 'player/config';
import { PluginModel } from 'player/plugins/model';
import { getTracker } from './pulse-stats/tracker';
import AdEvents, { type AdEventsAdData } from './pulse-stats/ad-events';
import PlaybackEvents, {
    type PlaybackEventData,
} from './pulse-stats/playback-events';
import UiEvents from './pulse-stats/ui-events';
import { CategoryTree } from '../model/category-tree';
import type { AssetModel } from '../model/asset';
import type {
    EventInput,
    Tracker,
    TrackerEventInput,
} from '@schibsted/pulse-sdk';
import JWPlayer = jwplayer.JWPlayer;

type Experiment = {
    id: number;
    variant: { id: number; Type: 'Subscription' | 'Login' };
};

/**
 * Retrieve tags from asset
 */
const getTags = (asset: AssetModel) =>
    (asset.get('tags') || [])
        .filter((tag) => utils.isObject(tag) || !isEmpty(tag.tag))
        .map(({ tag }) => tag);

export const getMediaAssetId = (provider: string, mediaId: number) =>
    `sdrn:${provider}:mediaasset:${mediaId}`;

export const getAssetLevel = (
    accessControl: string[],
    provider: string,
    experiment?: Experiment,
): 'Free' | 'Login' | 'Paid' => {
    if (accessControl.length > 0) {
        if (
            accessControl.includes('login') ||
            // special case for vgtv configuration - premium is login :)
            (provider === 'vgtv' && accessControl.includes('premium'))
        ) {
            return 'Login';
        }
        return 'Paid';
    }
    if (experiment?.variant?.Type) {
        if (experiment.variant.Type === 'Subscription') {
            return 'Paid';
        }
        if (experiment.variant.Type === 'Login') {
            return 'Login';
        }
    }
    return 'Free';
};

export const getAssetLevelName = (
    accessControl: string[],
    experiment?: Experiment,
): string => {
    if (accessControl.length > 0) {
        return accessControl.join(',');
    }
    if (experiment?.variant?.Type) {
        return experiment.variant.Type;
    }
    return 'Free';
};

const getImageUrl = (asset: AssetModel) => {
    const image = asset.getThumbnail();

    if (image) {
        return `${image}?t[]=480q80`;
    }

    return undefined;
};

type PulseStatsPluginOptions = {
    provider?: string;
    decorator?: <T>(data: T, asset: AssetModel) => T;
    engagementIntervals?: { live?: number; vod?: number };
    embed?: boolean;
    previousAssetId?: number;
    tts?: { enabled: boolean };
};

interface PlaybackTrackingData extends TrackerEventInput {
    position: number;
    duration: number;
    start?: boolean;
    object: {
        '@id': string;
        '@type': string;
        category?: number | string;
        duration?: number;
        assetType: string;
        name: string;
        muted: boolean;
        fullscreen: boolean;
        accessLevel: 'Free' | 'Login' | 'Paid';
        accessLevelName: string;
        autoplay: boolean;
        adSequenceCount: number;
        adSequenceDuration?: number[];
        adSequencePosition?: number;
        mediaAssetId?: string;
        embed: boolean;
        streamType: 'live' | 'wasLive' | 'vod';
        adblock: boolean;
        apiUrl: string;
        preview: boolean;
        'spt:custom': Partial<Record<string, string>>;
        previousAssetId?: string;
        playbackSource?: string;
        title: string;
        tags?: string[];
        imageUrl?: string;
        originAssetId?: number;
    };
}

/**
 * Integration with Pulse Tracker
 * https://schibsted.ghe.com/spt-dataanalytics/pulse-autotracker
 */
class PulseStatsPlugin extends PluginModel<PulseStatsPluginOptions> {
    provider?: string;
    decorator: Exclude<PulseStatsPluginOptions['decorator'], undefined>;
    intervals: { live?: number; vod?: number };
    embed: boolean;
    previousAssetId?: number;
    tracker: null | Tracker;
    experiment?: Experiment;
    isExperimentEnabled?: boolean;
    isMockAsset?: boolean;
    setupErrorEventEmitted: boolean;
    tts: { enabled: boolean };
    playerAsset: AssetModel = undefined!;
    categoryTree?: CategoryTree;
    categoryPath?: string;
    adEvents?: AdEvents;
    playbackEvents?: PlaybackEvents;
    uiEvents?: UiEvents;

    constructor(
        options: PulseStatsPluginOptions,
        experiment?: Experiment,
        isExperimentEnabled?: boolean,
        isMockAsset?: boolean,
    ) {
        super(options);

        this.onAdEvent = this.onAdEvent.bind(this);
        this.onPlaybackEvent = this.onPlaybackEvent.bind(this);
        this.onUiEvent = this.onUiEvent.bind(this);

        this.provider = options.provider;
        this.decorator = options.decorator || ((data) => data);
        this.intervals = {
            ...(options?.engagementIntervals?.live && {
                live: options.engagementIntervals.live,
            }),
            ...(options?.engagementIntervals?.vod && {
                vod: options?.engagementIntervals?.vod,
            }),
        };
        this.embed = options.embed === true;
        this.previousAssetId = options.previousAssetId;
        this.tracker = null;
        this.experiment = experiment;
        this.isExperimentEnabled = isExperimentEnabled;
        this.isMockAsset = isMockAsset;
        this.setupErrorEventEmitted = false;
        this.tts = options.tts || { enabled: false };
    }

    getName() {
        return 'PulseStats';
    }

    get jw(): null | JWPlayer {
        try {
            return this.player.getJWPlayer();
        } catch {
            return null;
        }
    }

    get asset() {
        const asset = this.playerAsset;
        const additional = asset.get('additional') || {};
        const accessControl = Object.keys(additional.access || {});
        const tags = getTags(asset);

        const data: Pick<
            PlaybackTrackingData['object'],
            | 'assetType'
            | 'title'
            | 'duration'
            | 'imageUrl'
            | 'accessLevel'
            | 'accessLevelName'
            | 'tags'
            | 'category'
            | 'originAssetId'
        > = {
            assetType: capitalizeFirstLetter(asset.get('assetType')),
            title: asset.get('title'),
            // duration should be in seconds
            duration: Math.round(asset.get('duration') / 1000),
            imageUrl: getImageUrl(asset),
            accessLevel: getAssetLevel(
                accessControl,
                asset.get('provider'),
                this.experiment,
            ),
            accessLevelName: getAssetLevelName(accessControl, this.experiment),
        };

        if (tags.length > 0) {
            data.tags = tags;
        }

        // check if stream has category
        if (asset.getCategory()) {
            data.category = asset.getCategory().getId();
        }

        // delete duration from live assets
        if (asset.getStreamType() === 'live') {
            delete data.duration;
        }

        if (additional.originAssetId) {
            data.originAssetId = additional.originAssetId;
        }

        if (this.tts.enabled) {
            data.assetType = 'AudioTTS';
        }

        return data;
    }

    get apiUrl() {
        const { url } = configuration.api;
        const asset = this.playerAsset;
        const protocol = !url.startsWith('http') ? 'https:' : '';
        return `${protocol}${url}${asset.getVendor()}/assets/${asset.getId()}?appName=linkpulse`;
    }

    get isAutoplay() {
        const autoplay = this.player.config.get('autoplay');
        return autoplay === true || autoplay === 'viewable';
    }

    getCategoryPath(categoryId: unknown) {
        if (typeof categoryId !== 'number') {
            return null;
        }

        // check if id exist and create category path
        if (!this.categoryPath) {
            this.categoryPath = this.categoryTree!.getPath(categoryId)
                .map((category) => category.title.toLowerCase())
                .join(' > ');
        }

        return this.categoryPath;
    }

    /**
     * Track Pulse event
     * Defer tracking when Pulse library is not loaded yet
     */
    async track(eventName: 'engagementEvent', eventData: TrackerEventInput) {
        const tracker = await this.getTracker();
        const data = Object.assign(
            {},
            eventData,
            this.isExperimentEnabled && {
                experiments: [
                    {
                        id: this.experiment!.id,
                        variant: this.experiment!.variant
                            ? this.experiment!.variant.id
                            : '0',
                        platform: this.provider,
                    },
                ],
            },
        );
        logger('Pulse').log(JSON.stringify(data, null, 4));
        tracker?.track(eventName, data);
    }

    async trackMediaEvent(data: PlaybackTrackingData) {
        try {
            if (!this.isMockAsset) {
                await this.categoryTree?.fetch();
            }
            // category data is available on ready event
            const categoryPath = this.getCategoryPath(data.object.category);

            if (categoryPath) {
                data.object.category = categoryPath;
            }
            await this.track(
                'engagementEvent',
                this.decorator(data, this.playerAsset!),
            );
        } catch {
            logger('Pulse').log('Could not track event');
        }
    }

    getTrackerOrNull() {
        return new Promise((resolve) => {
            const resolveTracker = () => resolve(this.tracker);

            if (this.tracker !== null || this.setupErrorEventEmitted) {
                resolveTracker();
            } else {
                this.once('ready', resolveTracker);
                this.once('setupError', resolveTracker);
            }
        });
    }

    getTracker(): Promise<Tracker | null> {
        return new Promise((resolve) => {
            const resolveTracker = () => resolve(this.tracker);

            if (this.tracker === null) {
                this.once('ready', resolveTracker);
            } else {
                resolveTracker();
            }
        });
    }

    setup() {
        // prevent asset to be changed with playnext before stream completes
        // @ts-expect-error internal vs external type
        this.playerAsset = this.player.getAsset({
            shouldLogDeprecatedWarning: false,
        });

        this.categoryTree = CategoryTree.getInstance(
            this.playerAsset.getVendor(),
        );

        this.adEvents = new AdEvents(this.player);
        this.playbackEvents = new PlaybackEvents(this.player, {
            vodInterval: this.intervals.vod,
            liveInterval: this.intervals.live,
        });
        this.uiEvents = new UiEvents(this.player);

        // initialize only once
        Promise.all([
            getTracker(),
            this.isMockAsset ? null : this.categoryTree!.fetch(),
        ])
            .then((data) => this.onReady(data))
            .catch(() => {
                this.setupErrorEventEmitted = true;
                this.trigger('setupError');
            });

        this.listenTo(this.adEvents, 'all', this.onAdEvent);
        if (!this.isMockAsset) {
            this.listenTo(this.playbackEvents, 'all', this.onPlaybackEvent);
        }
        this.listenTo(this.uiEvents, 'all', this.onUiEvent);
    }

    onReady(data: [Tracker | null, unknown]) {
        const [tracker] = data;

        this.tracker = tracker;
        this.trigger('ready');
    }

    attachSessionData(
        trackingData: PlaybackTrackingData,
        data: PlaybackEventData,
    ) {
        const asset = this.playerAsset;
        if (data.previousAssetId) {
            trackingData.object.previousAssetId = getMediaAssetId(
                asset.getVendor(),
                data.previousAssetId,
            );
            trackingData.object.playbackSource = data.playbackSource;
        } else if (this.previousAssetId) {
            trackingData.object.previousAssetId = getMediaAssetId(
                asset.getVendor(),
                this.previousAssetId,
            );
            trackingData.object.playbackSource = 'config';
        }
    }

    onPlaybackEvent(
        intent: string,
        data: PlaybackEventData = { playMethod: null, duration: 0 },
    ) {
        const asset = this.playerAsset;

        /**
         * Autoplay is not set for load method thus we use value set in player constructor
         * For other events method of playback start is checked
         * Autoplay is set to true when player actually autostarts or when its started with external call (.play())
         */
        const trackingData: PlaybackTrackingData = {
            intent,
            '@type': 'Engagement',
            position: data.position ?? 0,
            // duration is in ms
            duration: data.duration * 1000,
            object: {
                '@id': getMediaAssetId(asset.getVendor(), asset.getId()),
                '@type': 'MediaAsset',
                name: asset.getTitle(),
                muted: Boolean(this.jw?.getMute()),
                fullscreen: Boolean(this.jw?.getFullscreen()),
                autoplay:
                    data.playMethod === null
                        ? this.isAutoplay
                        : data.playMethod === 'autostart' ||
                          data.playMethod === 'external' ||
                          data.playMethod === 'viewable',
                adSequenceCount: data.count ?? 0,
                embed: this.embed,
                streamType: asset.getStreamType(),
                adblock: Boolean(this.jw?.getAdBlock()),
                apiUrl: this.apiUrl,
                preview: Boolean(this.player.config.get('videoPreview')),
                'spt:custom': {
                    'spt:streamProviderId': asset.getVendor(),
                },
                ...this.asset,
            },
        };

        const { adSequenceDuration } = this.adEvents!.adData;
        if (adSequenceDuration) {
            trackingData.object.adSequenceDuration = adSequenceDuration;
            trackingData.object.adSequenceCount = adSequenceDuration.length;
        }

        if (data.start === true) {
            trackingData.start = true;
        }

        this.attachSessionData(trackingData, data);

        this.trackMediaEvent(trackingData);
    }

    onAdEvent(intent: string, data: AdEventsAdData) {
        const asset = this.playerAsset;
        const trackingData: PlaybackTrackingData = {
            intent,
            '@type': 'Engagement',
            position: data.position ?? 0,
            // position and duration are equal because ads does not allow to seek through content
            // duration is in ms
            duration: data.position ? data.position * 1000 : 0,
            object: {
                '@id': `sdrn:${this.provider}:mediaad:${data.id}`,
                '@type': 'MediaAd',
                assetType: capitalizeFirstLetter(data.type!),
                muted: Boolean(this.jw?.getMute()),
                fullscreen: Boolean(this.jw?.getFullscreen()),
                autoplay: this.isAutoplay,
                mediaAssetId: getMediaAssetId(asset.getVendor(), asset.getId()),
                adSequenceCount: data.count ?? 0,
                adblock: Boolean(this.jw?.getAdBlock()),
                apiUrl: this.apiUrl,
            } as Partial<
                PlaybackTrackingData['object']
            > as PlaybackTrackingData['object'],
            // the above is a trick to skip some MediaAsset fields
        };

        if (data.placementId) {
            trackingData.object['spt:custom'] = {
                ...trackingData.object['spt:custom'],
                'spt:adPlacementId': data.placementId,
            };
        }

        if (data.adSequenceDuration) {
            trackingData.object.adSequenceDuration = data.adSequenceDuration;
            trackingData.object.adSequenceCount =
                data.adSequenceDuration.length;
        }

        if (data.sequence) {
            trackingData.object.adSequencePosition = data.sequence;
            if (
                data.adSequenceDuration &&
                data.adSequenceDuration[data.sequence - 1]
            ) {
                trackingData.object.duration =
                    data.adSequenceDuration[data.sequence - 1];
            }
        }

        if (data.start === true) {
            trackingData.start = true;
        }

        if (asset.getCategory()) {
            trackingData.object.category = this.categoryTree
                ?.getPath(asset.getCategory()?.getId())
                .map((i) => i.title)
                .join(' > ')
                .toLocaleLowerCase();
        }

        this.attachSessionData(trackingData, this.playbackEvents!.eventData);

        return this.trackMediaEvent(trackingData);
    }

    async onUiEvent<T extends { id: number }>(_eventName: string, data: T) {
        const asset = this.playerAsset;
        const trackingData: EventInput = {
            '@type': 'Engagement',
            object: {
                ...data,
                '@id': getMediaAssetId(asset.getVendor(), data.id),
                '@type': 'UIElement',
                apiUrl: this.apiUrl,
            },
        };

        this.track('engagementEvent', trackingData);
    }

    destroy() {
        this.stopListening();

        this.adEvents?.destroy();
        this.playbackEvents?.destroy();
    }
}

export default PulseStatsPlugin;
